/**
 * Public landing API – active semester and approved classes for the landing page.
 * No auth. When VITE_API_BASE_URL is unset or requests fail, returns null so the app can use static fallback.
 */

import type {
  ApiResponse,
  PublicClassDetail,
  PublicClassListItem,
  ScheduleColumnDefinition,
  SiteAnnouncement,
} from '@lambda/shared/types';
import { apiFetchUrl } from '@constants/apiConfig';
import {
  ACTIVE_SEMESTER_CACHE_MS,
  ACTIVE_SEMESTER_CACHE_MS_NONE,
  PUBLIC_CLASSES_CACHE_MS,
} from '@constants/activeSemesterSync';
import {
  clearPublicLandingSemesterAbsentCache,
  clearPublicLandingSemesterCache,
  readPublicLandingSemesterAbsentCache,
  readPublicLandingSemesterCache,
  writePublicLandingSemesterAbsentCache,
  writePublicLandingSemesterCache,
} from '@utils/publicLandingCache';
import { resolvePublicClassImageSrc } from '@utils/publicClassImage';

export interface ActiveSemesterPayload {
  semesterId: string;
  name: string;
  startDate: number;
  endDate: number;
  /** Unique teachers on approved, scheduled classes (any role that teaches). */
  teacherCount?: number;
  /** Count of approved classes for the active semester. */
  classCount?: number;
  /**
   * Average approved, scheduled classes covering each grade K–12
   * (preschool and adult excluded). Whole number for At a Glance.
   */
  avgK12ClassesPerGrade?: number;
  /** Custom schedule column definitions (null = use defaults). */
  scheduleColumns?: ScheduleColumnDefinition[] | null;
  /** Make-up Thursday date in ms (for schedule PDF). */
  makeupDate?: number | null;
  /** Footer blurb for schedule PDF (extra rooms, shared spaces). */
  scheduleExtraRoomsNotes?: string | null;
}

/** Public catalog row; same shape as `PublicClassListItem` from shared types. */
export type PublicClassItem = PublicClassListItem;

export interface GetActiveSemesterOptions {
  /** Bypass in-memory and session caches (e.g. manual refresh or admin activate). */
  force?: boolean;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T> | null> {
  try {
    const res = await fetch(apiFetchUrl(path), {
      ...options,
      headers: { 'Content-Type': 'application/json', ...(options.headers as Record<string, string>) },
    });
    const data = (await res.json().catch(() => ({}))) as ApiResponse<T>;
    if (!res.ok) return null;
    return data;
  } catch {
    return null;
  }
}

type ActiveSemesterFetchResult = {
  data: ActiveSemesterPayload | null;
  /** True only when the API confirmed there is no active semester (404). */
  confirmedAbsent: boolean;
};

let activeSemesterCache: { data: ActiveSemesterPayload | null; fetchedAt: number } | null = null;
let activeSemesterInflight: Promise<ActiveSemesterFetchResult> | null = null;

function activeSemesterCacheTtlMs(data: ActiveSemesterPayload | null): number {
  return data ? ACTIVE_SEMESTER_CACHE_MS : ACTIVE_SEMESTER_CACHE_MS_NONE;
}

function isActiveSemesterCacheFresh(
  cache: { data: ActiveSemesterPayload | null; fetchedAt: number },
  now: number
): boolean {
  return now - cache.fetchedAt < activeSemesterCacheTtlMs(cache.data);
}

async function fetchActiveSemesterUncached(): Promise<ActiveSemesterFetchResult> {
  try {
    const res = await fetch(apiFetchUrl('/public/semesters/active'), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = (await res.json().catch(() => ({}))) as ApiResponse<ActiveSemesterPayload>;
    if (res.status === 404) {
      return { data: null, confirmedAbsent: true };
    }
    if (!res.ok) {
      return { data: null, confirmedAbsent: false };
    }
    if (!json?.success || !json.data) {
      return { data: null, confirmedAbsent: false };
    }
    return { data: json.data, confirmedAbsent: false };
  } catch {
    return { data: null, confirmedAbsent: false };
  }
}

/** Drop in-memory and session caches so the next fetch hits the API. */
export function invalidateActiveSemesterCache(): void {
  activeSemesterCache = null;
  activeSemesterInflight = null;
  clearPublicLandingSemesterCache();
  clearPublicLandingSemesterAbsentCache();
  invalidatePublicClassesCache();
}

type PublicClassesCacheEntry = {
  /** Successful catalog only — failures are never cached. */
  data: PublicClassItem[];
  fetchedAt: number;
};

const publicClassesCache = new Map<string, PublicClassesCacheEntry>();
const publicClassesInflight = new Map<string, Promise<PublicClassItem[] | null>>();

/** Drop cached public class catalogs (one semester or all). */
export function invalidatePublicClassesCache(semesterId?: string): void {
  if (semesterId) {
    publicClassesCache.delete(semesterId);
    publicClassesInflight.delete(semesterId);
    return;
  }
  publicClassesCache.clear();
  publicClassesInflight.clear();
}

async function fetchPublicClassesUncached(semesterId: string): Promise<PublicClassItem[] | null> {
  const encoded = encodeURIComponent(semesterId);
  const response = await request<{ classes: PublicClassItem[]; count: number }>(
    `/public/classes?semesterId=${encoded}`,
    { method: 'GET' }
  );
  if (!response?.success || !response.data?.classes) return null;
  // Uploaded covers arrive as relative API paths; resolve to absolute for <img src>.
  return response.data.classes.map((cls) => ({
    ...cls,
    imageUrl: resolvePublicClassImageSrc(cls.imageUrl),
  }));
}

/**
 * Fetch the active semester. Returns null if no API, no active semester, or error.
 * Caches longer when no semester exists (setup phase) to avoid repeated 404s.
 */
export async function getActiveSemester(
  options?: GetActiveSemesterOptions
): Promise<ActiveSemesterPayload | null> {
  const force = options?.force ?? false;
  const now = Date.now();

  if (!force && activeSemesterCache && isActiveSemesterCacheFresh(activeSemesterCache, now)) {
    return activeSemesterCache.data;
  }

  if (!force) {
    const sessionHit = readPublicLandingSemesterCache();
    if (sessionHit) {
      activeSemesterCache = { data: sessionHit, fetchedAt: now };
      return sessionHit;
    }
    if (readPublicLandingSemesterAbsentCache()) {
      activeSemesterCache = { data: null, fetchedAt: now };
      return null;
    }
  }

  if (!activeSemesterInflight) {
    activeSemesterInflight = fetchActiveSemesterUncached().finally(() => {
      activeSemesterInflight = null;
    });
  }
  const result = await activeSemesterInflight;
  if (result.data) {
    activeSemesterCache = { data: result.data, fetchedAt: Date.now() };
    writePublicLandingSemesterCache(result.data);
    clearPublicLandingSemesterAbsentCache();
    return result.data;
  }
  if (result.confirmedAbsent) {
    activeSemesterCache = { data: null, fetchedAt: Date.now() };
    clearPublicLandingSemesterCache();
    writePublicLandingSemesterAbsentCache();
  }
  return null;
}

export interface GetPublicClassesOptions {
  /** Bypass short-TTL cache (e.g. manual Explore refresh). */
  force?: boolean;
}

/**
 * Fetch approved classes for a semester. Returns null if no API or error.
 * Short in-memory TTL + inflight dedupe per browser tab (successful responses only).
 */
export async function getPublicClasses(
  semesterId: string,
  options?: GetPublicClassesOptions
): Promise<PublicClassItem[] | null> {
  const force = options?.force ?? false;
  const now = Date.now();

  if (!force) {
    const cached = publicClassesCache.get(semesterId);
    if (cached && now - cached.fetchedAt < PUBLIC_CLASSES_CACHE_MS) {
      return cached.data;
    }
    const inflight = publicClassesInflight.get(semesterId);
    if (inflight) return inflight;
  }

  const pending = fetchPublicClassesUncached(semesterId).then((data) => {
    // Never cache failures as an empty catalog — that hid classes for the full TTL.
    if (data != null) {
      publicClassesCache.set(semesterId, { data, fetchedAt: Date.now() });
    }
    return data;
  });
  publicClassesInflight.set(semesterId, pending);
  try {
    return await pending;
  } finally {
    if (publicClassesInflight.get(semesterId) === pending) {
      publicClassesInflight.delete(semesterId);
    }
  }
}

/** Single approved class in the active semester (no auth). */
export async function getPublicClass(classId: string): Promise<PublicClassDetail | null> {
  const response = await request<{ class: PublicClassDetail }>(
    `/public/classes/${encodeURIComponent(classId)}`,
    { method: 'GET' }
  );
  if (!response?.success || !response.data?.class) return null;
  const cls = response.data.class;
  return { ...cls, imageUrl: resolvePublicClassImageSrc(cls.imageUrl) };
}

/** Active announcements flagged for the public landing page (no auth). */
export async function getPublicLandingAnnouncements(): Promise<SiteAnnouncement[] | null> {
  const response = await request<{ announcements: SiteAnnouncement[] }>(
    '/public/announcements/active',
    { method: 'GET' }
  );
  if (!response?.success || !response.data?.announcements) return null;
  return response.data.announcements;
}
